import React, { useState, useMemo } from 'react';
import {
  AVATARS, AVATAR_COLOURS, AVATAR_CATEGORIES, INITIALS_INK,
  avatarById, colourById, resolveAvatarId, defaultColourFor,
} from './avatars/avatarSet';

/**
 * Supply Pilot — Avatar Picker
 *
 * A self-contained React component that lets users choose from 35 avatars
 * (5 classic, 10 fun, 20 picture) styled to Fusion 2K brand guidelines.
 * Picture avatars are icon-only — they don't render initials.
 *
 * Props:
 *   initials       — string, the user's initial(s) to render inside avatars (default "R")
 *   initialId      — string, the avatar id to show as pre-selected (default "signature")
 *   onSave(id)     — function called with the chosen avatar id when "Save" is clicked
 *   onCancel()     — function called when "Cancel" is clicked
 *
 * Usage:
 *   <AvatarPicker initials="R" onSave={(id) => updateUser({ avatar: id })} />
 *
 * To render a saved avatar elsewhere in the app, use the <Avatar /> component below:
 *   <Avatar id="signature" initials="R" size={36} />
 */

// ----- Avatar art -------------------------------------------------------------
// The illustrated set (72 icons across 8 categories, Otto first), the 9
// background colours, keywords and the legacy id mapping all live in
// ./avatars/avatarSet. The old hand-rolled `renderers` map + AVATAR_GROUPS
// manifest were retired when the set was expanded — icons are data now, not a
// switch statement, so adding one is a record rather than a new function.

// ----- Reusable <Avatar /> component for use elsewhere ------------------------
// Use this anywhere in your app to render a chosen avatar.
//   <Avatar id="signature" initials="R" size={36} />
//   <Avatar url="https://..." initials="R" size={36} />  ← uploaded photo
// When url is set the image overrides the preset and renders with a
// Fusion-green ring around the edge.
export function Avatar({ id, colour, url, initials = 'R', size = 96, animated = false }) {
  if (url) {
    // 2px ring + 2px inner gap so the image floats inside the ring.
    // The image itself is rounded-full so it stays circular regardless
    // of source aspect (object-fit: cover does the heavy lifting; the
    // user already cropped to a circle on upload).
    //
    // 2026-06 design-system: ring muted from Fusion Green #92D400 to a
    // subtle dark grey. The avatar IS the user — the ring is chrome
    // around it, not a status signal, so it shouldn't pulse in the
    // brand-primary green. The mascot artwork stays untouched.
    const padding = Math.max(2, Math.round(size * 0.04));
    return (
      <span
        style={{
          display: 'inline-block',
          width: size,
          height: size,
          borderRadius: '50%',
          border: '2px solid #3a3c34',
          padding,
          boxSizing: 'border-box',
          background: '#131313',
        }}
        aria-label={initials ? `${initials} avatar` : 'Avatar'}>
        <img
          src={url}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      </span>
    );
  }
  // Illustrated set: an icon on an independently-chosen background colour.
  // `id` may be a legacy value from before the set was expanded — resolve it
  // so existing users keep their avatar where the icon survived.
  const resolvedId = resolveAvatarId(id);
  const colourId = colour || defaultColourFor(resolvedId);
  const swatch = colourById(colourId) || colourById('grey');
  const label = resolvedId === 'initials'
    ? `${initials} avatar`
    : `${avatarById(resolvedId)?.name || 'Avatar'} avatar`;

  const shell = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size,
    height: size,
    borderRadius: '50%',
    background: swatch.bg,
    flexShrink: 0,
  };

  // Initials are a real option, not a fallback — plenty of people don't want
  // a cartoon frog representing them at work.
  if (resolvedId === 'initials') {
    return (
      <span style={shell} role="img" aria-label={label}>
        <span style={{
          fontFamily: "'Metropolis', Manrope, sans-serif",
          fontWeight: 800,
          fontSize: Math.round(size * 0.36),
          letterSpacing: '-0.02em',
          color: INITIALS_INK[colourId] || '#55605a',
        }}>{initials}</span>
      </span>
    );
  }

  const avatar = avatarById(resolvedId);
  return (
    <span style={shell} role="img" aria-label={label}>
      {/* Otto brings his own viewBox (he IS the mascot component used across
          the app, not a lookalike), so he draws himself at the requested size.
          Everything else is a 40-unit icon at 80% of the circle. */}
      {avatar?.render ? avatar.render(size) : (
        <svg
          width={Math.round(size * 0.8)}
          height={Math.round(size * 0.8)}
          viewBox="0 0 40 40"
          fill="none"
          aria-hidden="true"
          style={{ display: 'block' }}>
          {avatar?.svg}
        </svg>
      )}
    </span>
  );
}


// ----- Inline styles ----------------------------------------------------------
// Keyframes injected once via a <style> tag so we don't need a CSS file.
const KEYFRAMES = `
@keyframes sp-wink { 0%,90%,100% { transform: scaleY(1); } 93%,97% { transform: scaleY(0.1); } }
@keyframes sp-flame { from { transform: scaleY(1); opacity: 0.9; } to { transform: scaleY(1.25); opacity: 1; } }
@keyframes sp-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes sp-pulse1 { from { opacity: 0.3; } to { opacity: 1; } }
@keyframes sp-pulse2 { from { opacity: 1; } to { opacity: 0.3; } }
@keyframes sp-swish { 0%,100% { transform: rotate(-10deg); } 50% { transform: rotate(20deg); } }
@keyframes sp-confetti1 { 0% { transform: translate(0,0) rotate(0deg); opacity: 1; } 100% { transform: translate(-14px,-18px) rotate(180deg); opacity: 0; } }
@keyframes sp-confetti2 { 0% { transform: translate(0,0) rotate(0deg); opacity: 1; } 100% { transform: translate(12px,-16px) rotate(-180deg); opacity: 0; } }
@keyframes sp-confetti3 { 0% { transform: translate(0,0) rotate(0deg); opacity: 1; } 100% { transform: translate(-10px,-22px) rotate(220deg); opacity: 0; } }
@keyframes sp-confetti4 { 0% { transform: translate(0,0) rotate(0deg); opacity: 1; } 100% { transform: translate(16px,-20px) rotate(-140deg); opacity: 0; } }
@keyframes sp-wave { 0%,100% { transform: skewX(0deg); } 50% { transform: skewX(-12deg); } }
@keyframes sp-float1 { 0% { transform: translate(0,0); opacity: 0.9; } 100% { transform: translate(-6px,-16px); opacity: 0; } }
@keyframes sp-float2 { 0% { transform: translate(0,0); opacity: 0.9; } 100% { transform: translate(8px,-18px); opacity: 0; } }
@keyframes sp-bolt { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.7; transform: scale(1.08); } }
@keyframes sp-bolt-bg { 0%,100% { fill: #131313; } 50% { fill: #1f2200; } }
@keyframes sp-flicker { from { transform: scaleY(1) rotate(-2deg); } to { transform: scaleY(1.15) rotate(2deg); } }
@keyframes sp-bubble1 { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(-10px); opacity: 0; } }
@keyframes sp-bubble2 { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(-12px); opacity: 0; } }
@keyframes sp-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
@keyframes sp-steam { 0% { transform: translateY(4px); opacity: 0; } 30% { opacity: 0.7; } 100% { transform: translateY(-10px); opacity: 0; } }
@keyframes sp-pant { from { transform: scaleY(1); } to { transform: scaleY(1.5); } }
@keyframes sp-quack { 0%,100% { transform: rotate(0deg); } 50% { transform: rotate(-8deg); } }
@keyframes sp-nose-twitch { 0%,100% { transform: translateY(0); } 30%,60% { transform: translateY(-1px); } }
@keyframes sp-flap1 { 0%,100% { transform: rotate(0deg); } 50% { transform: rotate(-25deg); } }
@keyframes sp-flap2 { 0%,100% { transform: rotate(0deg); } 50% { transform: rotate(25deg); } }
@keyframes sp-frog-blink { 0%,90%,100% { transform: scaleY(1); } 93%,97% { transform: scaleY(0.1); } }
@keyframes sp-sway { 0%,100% { transform: rotate(-3deg); } 50% { transform: rotate(3deg); } }
@keyframes sp-sun-glow { from { transform: scale(1); opacity: 1; } to { transform: scale(1.2); opacity: 0.85; } }
@keyframes sp-petal-sway { 0%,100% { transform: rotate(-4deg); } 50% { transform: rotate(4deg); } }
@keyframes sp-dpad { 0%,100% { transform: translateX(0); } 25% { transform: translateX(-1px); } 75% { transform: translateX(1px); } }
@keyframes sp-note1 { 0% { transform: translate(0,0); opacity: 1; } 100% { transform: translate(-4px,-10px); opacity: 0; } }
@keyframes sp-note2 { 0% { transform: translate(0,0); opacity: 1; } 100% { transform: translate(4px,-12px); opacity: 0; } }
@keyframes sp-heart-beat { 0%,100% { transform: scale(1); } 20%,40% { transform: scale(1.1); } }
@keyframes sp-sparkle1 { 0%,100% { opacity: 0; transform: scale(0.5); } 50% { opacity: 1; transform: scale(1.5); } }
@keyframes sp-sparkle2 { 0%,100% { opacity: 0; transform: scale(0.5); } 50% { opacity: 1; transform: scale(1.5); } }
@keyframes sp-rain { 0% { transform: translateY(-4px); opacity: 1; } 100% { transform: translateY(6px); opacity: 0; } }
`;

// Light-theme chrome (build brief #8). The page moved off the dark
// surface onto the admin light theme: sage page base, white cards, ink
// text. Green splits by job — #92D400 stays for the primary CTA, the
// selected-cell ring and the tick; deep green #009159 carries the
// green-caps labels/headings so they stay legible on the light base.
// The avatar SVG art (dark discs) is deliberately left untouched — it's
// the chosen avatar's identity, rendered the same everywhere.
const styles = {
  // Centred pop-out modal (not a full-page takeover) — the picker is a quick
  // "pick a face and get out" job, so it shouldn't feel like navigating away.
  overlay: {
    position: 'fixed', inset: 0, zIndex: 150,
    background: 'rgba(30,33,25,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24, overflowY: 'auto',
    fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, sans-serif',
  },
  modal: {
    background: '#fff', color: '#131313',
    border: '1px solid #E6EADB', borderRadius: 20,
    boxShadow: '0 1px 2px rgba(45,48,35,0.04), 0 24px 48px -16px rgba(45,48,35,0.28)',
    width: '100%', maxWidth: 640, maxHeight: 'calc(100vh - 48px)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '18px 22px 16px', borderBottom: '1px solid #EDF0E5', flexShrink: 0,
  },
  modalBody: { flex: 1, overflowY: 'auto', padding: '16px 22px 4px' },
  modalFooter: {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10,
    padding: '13px 22px', borderTop: '1px solid #EDF0E5', background: '#F7F9F0', flexShrink: 0,
  },
  closeBtn: {
    marginLeft: 'auto', width: 32, height: 32, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: 8, border: '1px solid #E6EADB', background: '#fff',
    color: '#51534A', cursor: 'pointer', padding: 0,
  },
  modalTitle: { fontFamily: "'Metropolis', Manrope, sans-serif", fontSize: 17, fontWeight: 800, color: '#131313', lineHeight: 1.2 },
  modalSelected: { fontFamily: "'Metropolis', Manrope, sans-serif", fontSize: 13, color: '#7C7F6F', marginTop: 2 },
  header: { textAlign: 'center', marginBottom: 12 },
  h1: { fontWeight: 700, color: '#009159', textTransform: 'uppercase', letterSpacing: '0.2em', fontSize: 22, marginBottom: 10 },
  sub: { color: '#4C4E42', fontSize: 14, marginBottom: 32, textAlign: 'center' },
  sectionHeading: { display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 },
  sectionLabel: { fontSize: 11, color: '#009159', textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 600 },
  sectionCount: { color: '#7C7F6F', fontSize: 11, fontWeight: 600, marginLeft: 'auto' },
  // 8-across at modal density. Names live in the header ("Selected: …") plus
  // each button's title/aria-label, rather than under every icon — at this
  // size a caption per cell would truncate to noise.
  grid: { display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 8, marginBottom: 18 },
  toolbar: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20 },
  search: {
    flex: '1 1 260px', minWidth: 200, padding: '10px 14px', borderRadius: 10,
    border: '1px solid #D4D9C6', background: '#fff', color: '#131313',
    fontFamily: "'Metropolis', Manrope, sans-serif", fontSize: 14, outline: 'none',
  },
  surprise: {
    padding: '10px 16px', borderRadius: 10, border: '1.5px solid #B7D9BF',
    background: '#fff', color: '#009159', cursor: 'pointer',
    fontFamily: "'Metropolis', Manrope, sans-serif", fontSize: 13, fontWeight: 700,
  },
  colourRow: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 28 },
  swatch: (bg, on) => ({
    width: 34, height: 34, borderRadius: '50%', background: bg, cursor: 'pointer',
    border: on ? '2px solid #131313' : '1px solid #D4D9C6',
    boxShadow: on ? '0 0 0 3px #fff, 0 0 0 5px #009159' : 'none',
    padding: 0,
  }),
  selectedBar: {
    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18,
    fontFamily: "'Metropolis', Manrope, sans-serif", fontSize: 13, color: '#4C4E42',
  },
  noResults: {
    padding: '18px 4px', color: '#7C7F6F', fontSize: 14,
    fontFamily: "'Metropolis', Manrope, sans-serif",
  },
  cell: (selected) => ({
    background: selected ? 'rgba(146, 212, 0, 0.12)' : '#FFFFFF',
    border: `1px solid ${selected ? '#92D400' : '#D4D9C6'}`,
    borderRadius: 8,
    padding: '20px 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    cursor: 'pointer',
    transition: 'all 0.2s',
    position: 'relative',
  }),
  cellName: (selected) => ({
    fontSize: 11,
    color: selected ? '#009159' : '#7C7F6F',
    textTransform: 'uppercase',
    letterSpacing: '0.2em',
    fontWeight: 600,
    textAlign: 'center',
  }),
  tick: {
    position: 'absolute', top: 8, right: 8, width: 18, height: 18, borderRadius: '50%',
    background: '#92D400', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  preview: {
    background: '#FFFFFF', border: '1px solid #D4D9C6', borderRadius: 8,
    padding: '20px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24,
  },
  previewLeft: { display: 'flex', alignItems: 'center', gap: 16 },
  previewLabel: { fontSize: 10, color: '#009159', textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 600, marginBottom: 4 },
  previewName: { fontSize: 16, fontWeight: 700, color: '#131313' },
  previewRole: { fontSize: 12, color: '#7C7F6F', marginTop: 2 },
  btnRow: { display: 'flex', gap: 10 },
  btn: (primary) => ({
    padding: '10px 20px',
    borderRadius: 6,
    border: `1px solid ${primary ? '#92D400' : '#D4D9C6'}`,
    background: primary ? '#92D400' : '#FFFFFF',
    color: '#131313',
    fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
    transition: 'all 0.15s',
  }),
};


// Lightweight circular-crop on a 320×320 canvas. The user pans the
// image with mouse drag and scales with a slider; on save the visible
// circular area is rendered to a 300×300 output canvas, exported as
// PNG, and handed back via onSave(blob). Canvas-based to avoid
// pulling in a crop library for what's ultimately a hundred lines.


// ----- The picker component ---------------------------------------------------
//
// Selection model:
//   selected = 'custom'        → use uploaded photo at uploadedUrl
//   selected = '<preset_id>'    → use that preset's SVG renderer
//
// onSave receives (id, url) so the caller can write both
// internal_users.avatar_id and internal_users.avatar_url. When the
// user has a preset selected, url is null. The Avatar component
// renders whichever takes precedence (url wins over id).
export default function AvatarPicker({
  initials = 'R',
  initialId = 'initials',
  initialColour = null,
  initialUrl = null,
  currentUserId = null,
  userName = 'Robynne',
  userRole = 'Events Manager',
  onSave,
  onCancel,
}) {
  const [selected, setSelected] = useState(initialUrl ? 'custom' : initialId);
  const [hoveredId, setHoveredId] = useState(null);
  const [search, setSearch] = useState('');
  // Icon and background colour are chosen INDEPENDENTLY and stored as a pair.
  const [selectedColour, setSelectedColour] = useState(
    initialColour || defaultColourFor(resolveAvatarId(initialId))
  );

  // Search matches name AND keywords — critical where the name is a joke
  // ("Phil's Campa" must be findable by typing "van").
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return AVATARS;
    return AVATARS.filter(a =>
      a.name.toLowerCase().includes(q) ||
      a.keywords.some(k => k.includes(q))
    );
  }, [search]);

  // "Surprise me" — most people don't care and just want out of the dialog.
  const handleSurprise = () => {
    const pool = filtered.length ? filtered : AVATARS;
    const a = pool[Math.floor(Math.random() * pool.length)];
    const c = AVATAR_COLOURS[Math.floor(Math.random() * AVATAR_COLOURS.length)];
    setSelected(a.id);
    setSelectedColour(c.id);
  };

  // Name shown in the header — the real fallback for touch, which has no hover.
  const selectedName = selected === 'custom'
    ? 'Uploaded photo'
    : selected === 'initials'
      ? 'Initials'
      : (avatarById(resolveAvatarId(selected))?.name || 'Avatar');
  const selectedColourName = colourById(selectedColour)?.name || '';
  const [uploadedUrl] = useState(initialUrl);




  // A selection stores the PAIR — icon + colour, not just the icon. An
  // uploaded photo has no background colour of its own.
  const handleSaveAvatar = () => {
    if (selected === 'custom' && uploadedUrl) {
      onSave?.('custom', uploadedUrl, null);
    } else {
      onSave?.(selected, null, selectedColour);
    }
  };

  return (
    <div style={styles.overlay} onClick={onCancel} role="presentation">
      <style>{KEYFRAMES}</style>

      <div
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Choose your avatar">
        {/* Header — live preview + the selected name, which is the real
            fallback for touch (no hover, so no tooltip). */}
        <div style={styles.modalHeader}>
          <Avatar
            id={selected}
            colour={selectedColour}
            url={selected === 'custom' ? uploadedUrl : null}
            initials={initials}
            size={46} />
          <div style={{ minWidth: 0 }}>
            <div style={styles.modalTitle}>Choose your avatar</div>
            <div style={styles.modalSelected} aria-live="polite">
              Selected: <strong style={{ color: '#131313' }}>{selectedName}</strong>
              {selected !== 'custom' && selectedColourName ? ` · ${selectedColourName}` : ''}
            </div>
          </div>
          <button type="button" onClick={onCancel} aria-label="Close" style={styles.closeBtn}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div style={styles.modalBody}>

        {/* The photo-upload section was REMOVED on 2026-08-19 at Rob's request,
            and with it the whole pipeline behind it: the file input, the HEIC
            sniff, the canvas crop modal, and the storage write. Removing the
            tile alone would have left ~270 lines unreachable and four lint
            warnings on a build gate that is meant to mean something.

            AN EXISTING PHOTO STILL WORKS. `uploadedUrl` and the 'custom'
            selection path are untouched, so anyone who already uploaded one
            keeps it, sees it, and saves it. There is simply no longer a way to
            add a NEW one, and no way back to one once they pick an illustrated
            avatar instead.

            To restore: revert the commit. It is not a re-add of the tile. */}


        {/* Search + Surprise me — past ~40 options, scrolling stops working. */}
        <div style={styles.toolbar}>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search avatars — try “van”, “owl”, “coffee”…"
            aria-label="Search avatars by name or keyword"
            style={styles.search}
          />
          <button
            type="button"
            onClick={handleSurprise}
            style={styles.surprise}
            aria-label="Surprise me — pick a random avatar and colour">
            Surprise me
          </button>
        </div>

        {/* Background colour — chosen separately from the icon. If three
            people pick the cat, the avatar has failed at its only job. */}
        <div style={styles.sectionHeading}>
          <div style={styles.sectionLabel}>Background colour</div>
          <div style={styles.sectionCount}>
            {AVATAR_COLOURS.length} colours · {(AVATARS.length + 1) * AVATAR_COLOURS.length} combinations
          </div>
        </div>
        <div style={styles.colourRow} role="group" aria-label="Background colour">
          {AVATAR_COLOURS.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setSelectedColour(c.id)}
              aria-label={`${c.name} background`}
              aria-pressed={selectedColour === c.id}
              title={c.name}
              style={styles.swatch(c.bg, selectedColour === c.id)}
            />
          ))}
        </div>

        {AVATAR_CATEGORIES.map((cat) => {
          // Initials is a real option, not a fallback — surfaced as its own
          // deliberate choice rather than buried.
          const items = cat.id === 'initials'
            ? (search.trim() && !'initials'.includes(search.trim().toLowerCase()) ? [] : [{ id: 'initials', name: 'Initials' }])
            : filtered.filter((a) => a.category === cat.id);
          if (items.length === 0) return null;
          return (
            <div key={cat.id}>
              <div style={styles.sectionHeading}>
                <div style={styles.sectionLabel}>{cat.name}</div>
                <div style={styles.sectionCount}>{items.length} options</div>
              </div>

              <div style={styles.grid}>
                {items.map((item) => {
                  const isSelected = selected === item.id;
                  const isHovered = hoveredId === item.id;
                  // Where the name is a joke, the aria-label carries the plain
                  // word too — "Phil's Campa — campervan".
                  const plain = item.keywords?.[0];
                  const ariaLabel = plain && plain.toLowerCase() !== item.name.toLowerCase()
                    ? `${item.name} — ${plain}`
                    : item.name;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      aria-label={ariaLabel}
                      aria-pressed={isSelected}
                      title={ariaLabel}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 3, borderRadius: '50%', background: 'transparent',
                        border: isSelected ? '2px solid #4a7a0e' : '2px solid transparent',
                        cursor: 'pointer', font: 'inherit',
                        transform: isHovered ? 'scale(1.1)' : 'none',
                        transition: 'transform .12s',
                      }}
                      onClick={() => setSelected(item.id)}
                      onMouseEnter={() => setHoveredId(item.id)}
                      onMouseLeave={() => setHoveredId(null)}
                      onFocus={() => setHoveredId(item.id)}
                      onBlur={() => setHoveredId(null)}
                    >
                      <Avatar id={item.id} colour={selectedColour} initials={initials} size={44} />
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {search.trim() && filtered.length === 0 && (
          <div style={styles.noResults}>
            No avatars match “{search.trim()}”. Try a broader word — “car”, “bird”, “food”.
          </div>
        )}

        </div>{/* /modalBody */}

        <div style={styles.modalFooter}>
          <button style={styles.btn(false)} onClick={onCancel}>Cancel</button>
          <button style={styles.btn(true)} onClick={handleSaveAvatar}>Use this avatar</button>
        </div>
      </div>

      )}
    </div>
  );
}
